import React, { useEffect, useState, useCallback } from "react";
import { config } from "../../config/config";
import ClinicList from "./ClinicList";
import { Helmet } from "react-helmet";
import HomeSlider from "../Home/homeSlider";
import { BannerImg, BannerText, BannerWrapper } from "../Home/homeSlider";
import { styled } from "styled-components";
import { useDispatch } from "react-redux";
import { getClinicDetails } from "../../store/UserStore/Clinic/action";
import { useLocation } from "react-router";
import { get } from "../../helpers/api_helpers";
import { getLocalStorage,resolvePincode } from "../../helpers/utils";

const Clinics = () => {
  const [searchText, setSearchText] = useState("");
  const [locationReady, setLocationReady] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationError, setLocationError] = useState("");

  const dispatch = useDispatch();
  const location = useLocation();
  const serviceId = location?.state?.service_id;

  const [banners, setBanners] = useState([]);

  // ✅ LOGIN CHECK
  const isLoggedIn = !!getLocalStorage("AUTH_DETAILS");
  const locationDetails = !!getLocalStorage("LOCATION_DETAILS");

  const basicRequest = {
    page: 1,
    page_count: 9,
    keyword: "",
    service_id: serviceId || null,
  };

  const [request, setRequest] = useState(basicRequest);

  // // 🔥 Reverse geocode
  // const resolvePincode = async (lat, lon) => {
  //   try {
  //     const res = await fetch(
  //       `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`
  //     );
  //     const data = await res.json();
  //     setLocalStorage("LOCATION_DETAILS", data);
  //     console.log(data)
  //     return data?.address?.postcode || "";
  //   } catch {
  //     return "";
  //   }
  // };

  // 🔥 LOCATION FUNCTION WITH RETRY
  const requestLocation = useCallback((retry = 0) => {
    setLocationError("");

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const pincode = await resolvePincode(latitude, longitude);
        setRequest((r) => ({
          ...r,
          latitude,
          longitude,
          // pincode,
        }));

        setLocationReady(true);
        setLocationDenied(false);
      },
      (err) => {
        if (retry < 2) {
          setTimeout(() => requestLocation(retry + 1), 2000);
          return;
        }

        if (err.code === 1) {
          setLocationDenied(true);
        } else {
          setLocationError(
            "Unable to fetch location. Try again or move to an open area."
          );
        }

        setLocationReady(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  }, []);

  // ✅ HANDLE LOCATION BASED ON LOGIN
  useEffect(() => {
    if (!isLoggedIn) {
      requestLocation(); // Guest → ask location
    } else {
      setLocationReady(true); // Logged-in → skip location
    }
  }, [requestLocation, isLoggedIn]);

  // ✅ UPDATE SERVICE ID
  useEffect(() => {
    if (serviceId) {
      setRequest((prev) => ({
        ...prev,
        service_id: serviceId,
      }));
    }
  }, [serviceId]);

  // ✅ FETCH CLINICS
  useEffect(() => {
    if (locationReady) {
      dispatch(getClinicDetails({ request }));
    }
  }, [request, locationReady]);


  const handleSearchClick = (e) => {
    e.preventDefault();
    setRequest((prev) => ({ ...prev, keyword: searchText }));
  };

  // ✅ SHOW LOCATION SCREEN ONLY FOR GUESTS
  if (!locationReady && !isLoggedIn && !locationDetails) {
    return (
      <LocationOverlay>
        <LocationBox>
          <LocationIcon>📍</LocationIcon>
          <h2>Location Required</h2>
          <p>
            We need your location to show clinics near you.
            <br />
            Please allow location access.
          </p>

          {locationError && <ErrorText>{locationError}</ErrorText>}

          <AllowButton onClick={() => requestLocation()}>
            Enable Location
          </AllowButton>
        </LocationBox>
      </LocationOverlay>
    );
  }

  return (
    <>
          
        <HomeSlider page={'clinic'}/>
        <ClinicList /> 
      <Helmet>
        <title>Find Pet Clinics Near You | PetQura</title>
        <link rel="canonical" href="https://petqura.com/clinic" />
      </Helmet>

      <div>
        {/* ✅ Show only for guest users */}
        {locationDenied && !isLoggedIn && (
          <LocationBlocker>
            <div className="messageCard">
              <h3>Location Access Required</h3>
              <p>Please allow location access to view nearby clinics.</p>
              <button onClick={() => requestLocation()}>
                Enable Location
              </button>
            </div>
          </LocationBlocker>
        )}
      </div>
    </>
  );
};

export default Clinics;

const LocationBlocker = styled.div`
  width: 100%;
  padding: 15px 0;
  background: #fff1e8;
  border-bottom: 1px solid #ffd5bf;

  .messageCard {
    max-width: 760px;
    margin: 0 auto;
    padding: 16px;
    text-align: center;
  }

  h3 {
    margin: 0 0 8px;
    color: #8a2c00;
  }

  p {
    margin: 0 0 10px;
    color: #7a5d4a;
  }

  button {
    border: 0;
    padding: 10px 20px;
    border-radius: 8px;
    color: #fff;
    background: #cc5500;
  }`;
const LocationOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LocationBox = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 40px 32px;
  max-width: 420px;
  width: 90%;
  text-align: center;
  h2 { font-size: 22px; font-weight: 700; margin-bottom: 12px; color: #1a1a2e; }
  p { color: #555; font-size: 15px; line-height: 1.6; }
`;

const LocationIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const ErrorText = styled.p`
  color: #e53935 !important;
  font-size: 13px !important;
  margin-top: 10px;
`;

const AllowButton = styled.button`
  margin-top: 20px;
  background: #00419d;
  color: #fff;
  border: none;
  border-radius: 25px;
  padding: 12px 32px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #003080; }
`;

const SearchInputWrap = styled.form`
  display: flex;
  width: 300px;

  & input {
    border: 1px solid darkgray;
    padding: 10px;
    color: #8f8f8f;
    width: 80%;
    border-radius: 25px 0px 0px 25px;
    letter-spacing: 1px;
    font-size: 14px;
    background: transparent;
  }

  & button {
    width: 20%;
    padding: 10px;
    background-color: #00419d;
    border-radius: 0px 25px 25px 0px;
    text-align: center;

    & img {
      width: 20px;
      height: 20px;
    }
  }
`;


